// The frame every session mounts into (SPEC-redesign §4.6, §5.1). S0 owns
// index.html outright; S1–S4 never edit it and reach their part through `mount`.
export type MountPoint = 'topbar' | 'wheel' | 'kreska' | 'toolbar' | 'rail' | 'footer' | 'overlay';

/**
 * Under this block height there is no disc left worth the name and the circle
 * becomes the 72 px dial (§5.3, D-h, D-9). One number, because two parts answer
 * the same question about the same block and must not drift apart: the kreska
 * derives the block it is about to set, the wheel measures the block it has.
 * Only the number is shared — the two predicates measure different boxes.
 */
export const DIAL_UNDER = 130;

const IDS: Record<MountPoint, string> = {
  topbar: 'topbar',
  wheel: 'wheel',
  kreska: 'kreska',
  toolbar: 'toolbar',
  rail: 'rail',
  footer: 'footer',
  overlay: 'overlay',
};

/** The element a session renders into. Throws when the shell is not up. */
export function mount(name: MountPoint): HTMLElement {
  const el = document.getElementById(IDS[name]);
  if (!el) throw new Error(`the shell has no ${name}: index.html and src/shell.ts disagree`);
  return el;
}

/**
 * The install band's id, and the band itself when it is up (#348).
 *
 * Not a `MountPoint`: index.html does not carry the box and `mount` throws for
 * one it cannot find, while this band exists only where the browser can really
 * install the app. `src/screens/install.ts` puts it after the topbar and
 * `src/kreska/kreska.ts` takes its height off what the circle block may have —
 * two parts that may not import each other, and the frame is where they meet.
 */
export const INSTALL_ID = 'install';
export const installBand = (): HTMLElement | null => document.getElementById(INSTALL_ID);

/**
 * What the circle block and the rail have to share the viewport with: the
 * column's boxes that do not give. The install band counts only while it is up
 * — it is not in index.html, the resident can dismiss it, and above 900 px the
 * stylesheet hides it, so a box of no height takes nothing off the column.
 *
 * Here rather than in the kreska, which is its one caller: the band belongs to
 * `src/screens/install.ts` and the geometry to `src/kreska/kreska.ts`, two
 * parts that may not import each other (ADR-0013), and the frame is where they
 * meet.
 */
export function fixedBoxes(): number {
  return (
    mount('topbar').offsetHeight +
    (installBand()?.offsetHeight ?? 0) +
    mount('toolbar').offsetHeight +
    mount('footer').offsetHeight
  );
}

// §6.5: the page is ready when the data is ready **and** the map has either
// loaded or failed. A basemap that never answers must not hang the flag, or the
// map-failed state could not be tested — and a resident staring at a hatched
// disc is exactly who needs the rest of the screen to work.
let mapSettled = false;
let dataReady = false;

/**
 * Each half says which half it is, rather than the shell guessing from the DOM.
 * The map also says how it went, and this is the one writer of `html[data-map]`:
 * a wheel that fires `error` twice, or `error` after `load`, cannot mark the
 * data ready by accident.
 *
 * Data and (map ready or map failed) — see §6.5.
 */
export function markReady(what: 'data'): void;
export function markReady(what: 'map', how: 'ok' | 'failed'): void;
export function markReady(what: 'data' | 'map', how?: 'ok' | 'failed'): void {
  const root = document.documentElement;
  if (what === 'map') {
    // the first answer is the answer: a retry cannot turn a failed disc green
    // behind the resident's back, nor a loaded one hatched
    if (mapSettled) return;
    mapSettled = true;
    root.dataset.map = how ?? 'failed';
  } else {
    dataReady = true;
  }
  if (mapSettled && dataReady) root.dataset.ready = 'true';
}
